import { useState } from 'react';
import { Input, Select, Tag, Checkbox, Radio, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCountryArray } from '../../../utils/countryArray';
import { useCountryCodeInfos } from '../../owners/form/utils/useCountryCodeInfos';
import { createPhoneNumberFieldConfig } from '../../../utils/formFields/phoneNumberField';
import { createDocumentDraggerField } from '../../../utils/formFields/documentDraggerField';

const { Option } = Select;
const { TextArea } = Input;

const ANIMAL_TYPES = ['DOG', 'CAT', 'BIRD', 'EQUINE', 'FARM'];

/**
 * Additional Informations Fields
 * @hook
 *
 * @typedef {Object} AdditionalInformationsField
 * @property {string} label - The label of the field.
 * @property {string[]} name - The name of the field.
 * @property {Object} [input] - The input component for the field.
 * @property {Object[]} [rules] - The validation rules for the field.
 * @property {string} [initialValue] - The initial value for the field.
 * @property {string} [valuePropName] - The name of the prop that holds the value of the field.
 *
 * @typedef {Object} AdditionalInformationsFields
 * @property {AdditionalInformationsField[]} additionalInformationsFields - The list of additional information fields.
 *
 * @typedef {Object} AdditionalInformationsProps
 * @property {Function} setFilesList - The function to set the files list.
 * @property {Object} filesList - The list of files.
 * @property {boolean} isFieldsLoading - Indicates if the fields are loading.
 * @property {Object} form - The form object.
 * @property {Function} setInternetAccess - The function to set the internet access.
 * @property {boolean} internetAccess - Indicates if internet access is enabled.
 * @property {Function} setRemovedFiles - The function to set the removed files.
 * @property {string} country - The selected country.
 * @property {Function} setCountry - The function to set the selected country.
 *
 * @param {AdditionalInformationsProps} props - The component props.
 * @returns {AdditionalInformationsFields} The additional information fields.
 */
export const additionalInformations = ({
  setFilesList,
  filesList,
  isFieldsLoading,
  form,
  setInternetAccess,
  internetAccess,
  setRemovedFiles,
  country,
  setCountry
}) => {
  const { t } = useTranslation();
  const countries = useCountryArray();
  const { handleCountryCodeInfos } = useCountryCodeInfos(country);

  const [cityOptions, setCityOptions] = useState([]);

  const isAnimals = Form.useWatch(['is_animals'], form);
  const movingWithAnimal = Form.useWatch(['moving_with_animal'], form);

  const filterOption = (input, option) =>
    option.props.value.toLowerCase().indexOf(input.toLowerCase()) >= 0;

  const draggerProps = (name) => ({
    multiple: name === 'other_documents',
    onRemove: (file) => {
      if (name === 'other_documents') {
        const updatedFileList = { ...filesList };
        updatedFileList[name] = updatedFileList[name].filter(
          (item) => item.uid !== file.uid
        );
        setFilesList(updatedFileList);
        setRemovedFiles((prevRemovedFiles) => [...prevRemovedFiles, file.id]);
      } else {
        const updatedFileList = { ...filesList };
        setRemovedFiles((prevRemovedFiles) => [
          ...prevRemovedFiles,
          updatedFileList[name][0].id
        ]);
        delete updatedFileList[name];
        setFilesList(updatedFileList);
      }
    },
    beforeUpload: (file) => {
      if (name === 'other_documents') {
        setFilesList((prevFileList) => ({
          ...prevFileList,
          [name]: [...(prevFileList[name] || []), file]
        }));
      } else {
        setFilesList((prevFileList) => ({
          ...prevFileList,
          [name]: [file]
        }));
      }
      return false;
    },
    fileList: filesList[name]
  });

  const getRadioGroup = (onChange) => (
    <Radio.Group
      onChange={onChange}
      options={[
        { label: t('pensioners.form.yes'), value: true },
        { label: t('pensioners.form.no'), value: false }
      ]}
    />
  );

  const additionalInformationsFields = [
    {
      label: 'address.number',
      name: ['address', 'number'],
      rules: [{ required: true }]
    },
    {
      label: 'address.street',
      name: ['address', 'street'],
      rules: [{ required: true }]
    },
    {
      label: 'address.additional',
      name: ['address', 'additional']
    },
    {
      label: 'address.country',
      name: ['address', 'country'],
      input: (
        <Select
          showSearch
          filterOption={filterOption}
          isFieldsLoading={isFieldsLoading}
          onChange={(value) => {
            setCountry(value);
            form.setFieldValue(['address', 'postal_code'], undefined);
            form.setFieldValue(['address', 'city'], undefined);
            form.setFieldValue(['address', 'state'], undefined);
            setCityOptions([]);
          }}
        >
          {(countries || []).map((value) => (
            <Option key={value} value={value}>
              <Tag>{value}</Tag>
            </Option>
          ))}
        </Select>
      ),
      rules: [{ required: true }]
    },
    {
      label: 'address.postal_code',
      name: ['address', 'postal_code'],
      input: (
        <Input
          onChange={(e) => {
            const postalCode = e.target.value;

            form.setFieldValue(['address', 'city'], undefined);
            form.setFieldValue(['address', 'state'], undefined);
            setCityOptions([]);

            if (postalCode.length < 5) return;

            handleCountryCodeInfos(postalCode).then(
              ({ cities, city, state }) => {
                form.setFieldValue(['address', 'state'], state || undefined);

                if (cities.length > 1) {
                  setCityOptions(cities);
                } else {
                  form.setFieldValue(['address', 'city'], city || undefined);
                  setCityOptions([]);
                }
              }
            );
          }}
        />
      ),
      rules: [{ required: true }]
    },
    {
      label: 'address.state',
      name: ['address', 'state'],
      rules: [{ required: true }]
    },
    {
      label: 'address.city',
      name: ['address', 'city'],
      rules: [{ required: true }],
      input:
        cityOptions.length > 1 ? (
          <Select
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {cityOptions.map((city) => (
              <Option key={city} value={city}>
                {city}
              </Option>
            ))}
          </Select>
        ) : (
          <Input />
        )
    },
    createPhoneNumberFieldConfig('phone_number', 80),
    {
      name: ['internet_access'],
      initialValue: internetAccess,
      input: (
        <Checkbox
          defaultChecked={internetAccess}
          onChange={() => {
            if (!internetAccess) form.setFieldValue('email', undefined);
            setInternetAccess(!internetAccess);
          }}
        />
      ),
      valuePropName: 'checked'
    },
    ...(internetAccess
      ? [
          {
            name: ['email'],
            rules: [{ required: true }, { type: 'email' }]
          }
        ]
      : []),
    {
      name: ['is_animals'],
      input: getRadioGroup((e) => {
        if (!e.target.value) {
          form.setFieldValue(['liked_animals_types'], undefined);
        }
      })
    },
    ...(isAnimals
      ? [
          {
            name: ['liked_animals_types'],
            rules: [{ required: true }],
            input: (
              <Select mode="multiple" allowClear>
                {ANIMAL_TYPES.map((type) => (
                  <Option key={type} value={type}>
                    <Tag>{t(`animals.tags.${type}`)}</Tag>
                  </Option>
                ))}
              </Select>
            )
          }
        ]
      : []),
    {
      name: ['moving_with_animal'],
      input: getRadioGroup((e) => {
        if (!e.target.value) {
          form.setFieldValue(['animals_experiences'], undefined);
        }
      })
    },
    ...(movingWithAnimal
      ? [
          {
            name: ['animals_experiences'],
            rules: [{ required: true }],
            input: (
              <TextArea
                rows={4}
                value={(() => {
                  const val = form.getFieldValue(['animals_experiences']);
                  if (Array.isArray(val)) return val.join(', ');
                  return val;
                })()}
                onChange={(e) => {
                  form.setFieldValue(['animals_experiences'], e.target.value);
                }}
              />
            )
          }
        ]
      : []),
    {
      name: ['is_smoker'],
      input: getRadioGroup()
    },
    {
      name: ['is_newsletters'],
      input: getRadioGroup()
    },
    createDocumentDraggerField('early_retirement_certificate', draggerProps, t),
    createDocumentDraggerField('insurance_certificate', draggerProps, t),
    createDocumentDraggerField('residence_proof', draggerProps, t),
    createDocumentDraggerField('other_documents', draggerProps, t)
  ];

  return { additionalInformationsFields };
};
